import scenarios from "@/data/scenarios.json";

/**
 * App Specific Route Mapping
 *Like
 */
const nameMapping = {
  Settings: "Questions",
  Content: "Announcements"
};

/**
 * App Specific Configurations
 */
const config = {
  hasActiveEntry: false,
  extraColumnKeys: ["Pre-Lab", "Submitted", "Remaining"],
  evaluation: false,
  wideLayout: false
};

const TOTAL_SCENARIOS = scenarios.length;

/**
 * Returns true if the student has any meaningful data saved.
 */
function hasStarted(value) {
  if (!value || typeof value !== "object") return false;
  return (
    value.preLabComplete ||
    (Array.isArray(value.preLabRows) && value.preLabRows.length > 0) ||
    (value.scenarioDesigns && Object.keys(value.scenarioDesigns).length > 0)
  );
}

/**
 * Filters irrelevant entries (e.g. entries that don't have meaningful data) and evaluates them
 * @param {Array} entries - The entries to filter and evaluate
 * @returns {Array} - The filtered and evaluated entries
 */
function tableEntries(entries) {
  entries = entries.filter((entry) => hasStarted(entry.value));

  // Attach display columns — use spread to avoid mutating the Vuex-cached objects
  entries = entries.map((entry) => {
    const v = entry.value;
    const designs = v?.scenarioDesigns ?? {};
    const submitted = Object.entries(designs)
      .filter(([, d]) => d?.designSubmitted)
      .map(([id]) => id)
      .sort((a, b) => a.localeCompare(b));
    let submittedLabel;
    if (submitted.length === 0) {
      submittedLabel = "\u2014";
    } else if (submitted.length === TOTAL_SCENARIOS) {
      submittedLabel = "All";
    } else {
      submittedLabel = submitted.join(", ");
    }
    const remaining = TOTAL_SCENARIOS - submitted.length;
    return {
      ...entry,
      value: {
        ...v,
        "Pre-Lab": v?.preLabComplete ? "✓" : "—",
        Submitted: submittedLabel,
        Remaining: remaining > 0 ? `${remaining} left` : "Done"
      }
    };
  });

  return entries;
}

/**
 * Transforms entries for CSV/spreadsheet export.
 * Outputs one row per scenario per student.
 */
function exportEntries(entries) {
  const mappedEntries = [];

  entries.forEach((entry) => {
    const v = entry.value ?? {};
    const designs = v.scenarioDesigns ?? {};

    for (let scenarioId = 1; scenarioId <= TOTAL_SCENARIOS; scenarioId++) {
      const design = designs[scenarioId] ?? null;
      const validationResults = Array.isArray(design?.validationResult)
        ? design.validationResult
        : (design?.validationResult?.results ?? []);
      const hardFailures = validationResults
        .filter((r) => r.severity === "hard")
        .map((r) => r.message)
        .join("; ");
      const softWarnings = validationResults
        .filter((r) => r.severity === "soft")
        .map((r) => r.message)
        .join("; ");

      mappedEntries.push({
        fullname: entry.fullname,
        onid: entry.onid,
        preLabComplete: v.preLabComplete ? "Yes" : "No",
        preLabRows: v.preLabRows?.length ?? 0,
        pumpCurve_a: v.fittedCurve?.a ?? "",
        pumpCurve_b: v.fittedCurve?.b ?? "",
        pumpCurve_R2: v.fittedCurve?.R2 ?? "",
        scenarioId,
        designSubmitted: design?.designSubmitted ? "Yes" : "No",
        operatingFlow_GPM: design?.simResult?.operatingPoint?.Q_GPM ?? "",
        operatingHead_ft: design?.simResult?.operatingPoint?.H_ft ?? "",
        pumpEfficiency: design?.simResult?.pumpEfficiency ?? "",
        totalCost: design?.simResult?.totalCost ?? "",
        hardFailures: hardFailures || "None",
        softWarnings: softWarnings || "None",
        "Last Updated": entry.tsUpdatedLabel
      });
    }
  });

  return mappedEntries;
}

export { nameMapping, config, exportEntries, tableEntries };
